/**
 * Config API
 *
 * API client for config CRUD operations:
 * - List configs
 * - Get config
 * - Update config
 * - Get config history
 * - Promote config
 */

import type { ConfigType, Environment, TenantId } from "@tatvex/types";

import type { ApiResponse, ConfigMetadata, ConfigVersionEntry } from "../types";

import { apiRequest } from "./client";

// ============================================================================
// Request Types
// ============================================================================

interface ListConfigsRequest {
  readonly tenantId: TenantId | undefined;
  readonly environment: Environment;
  readonly verticalKey?: string;
  readonly configType?: ConfigType;
}

interface GetConfigRequest {
  readonly tenantId: TenantId | undefined;
  readonly environment: Environment;
  readonly path: string;
}

interface UpdateConfigRequest {
  readonly path: string;
  readonly content: string;
  readonly configType: ConfigType;
  readonly verticalKey: string;
  readonly changeSummary?: string;
}

interface GetConfigHistoryRequest {
  readonly tenantId: TenantId | undefined;
  readonly environment: Environment;
  readonly path: string;
}

interface PromoteConfigRequest {
  readonly tenantId: TenantId | undefined;
  readonly sourceEnvironment: Environment;
  readonly targetEnvironment: Environment;
  readonly path: string;
}

// ============================================================================
// Response Types
// ============================================================================

interface ConfigListItem {
  readonly path: string;
  readonly configType: ConfigType;
  readonly verticalKey: string;
  readonly version: number;
  readonly status: "draft" | "live" | "archived";
  readonly checksum: string;
  readonly createdBy: string;
  readonly createdAt: string;
}

interface ConfigResponse {
  readonly content: string;
  readonly metadata: ConfigMetadata;
}

interface UpdateConfigResponse {
  readonly path: string;
  readonly version: number;
  readonly checksum: string;
  readonly updatedAt: string;
}

interface PromoteConfigResponse {
  readonly sourcePath: string;
  readonly targetPath: string;
  readonly sourceVersion: number;
  readonly targetVersion: number;
  readonly promotedAt: string;
}

// ============================================================================
// API Functions
// ============================================================================

// ============================================================================
// API Response Types (from API)
// ============================================================================

interface VerticalConfigInfo {
  readonly configType: string;
  readonly liveVersion: number | null;
  readonly draftExists: boolean;
  readonly totalVersions: number;
  readonly lastModifiedAt: string | null;
}

interface VerticalInfo {
  readonly verticalKey: string;
  readonly configs: readonly VerticalConfigInfo[];
}

interface ListConfigsApiResponse {
  readonly data: {
    readonly verticals: readonly VerticalInfo[];
    readonly totalVerticals: number;
  };
  readonly timestamp: string;
  readonly requestId: string;
}

/**
 * List all configs for a tenant/environment.
 * Transforms the nested API response into a flat config list.
 */
async function listConfigs(
  request: ListConfigsRequest
): Promise<ApiResponse<readonly ConfigListItem[]>> {
  const params = new URLSearchParams();

  if (request.verticalKey) {
    params.set("verticalKey", request.verticalKey);
  }
  if (request.configType) {
    params.set("configType", request.configType);
  }

  const queryString = params.toString();
  const endpoint = `/api/v1/configs${queryString ? `?${queryString}` : ""}`;

  const response = await apiRequest<ListConfigsApiResponse>(endpoint);

  if (!response.success) {
    return response as ApiResponse<readonly ConfigListItem[]>;
  }

  // Transform nested verticals into flat config list
  const configs: ConfigListItem[] = [];

  // API returns { data: { verticals: [...] }, ... } - access nested data
  const apiData = response.data as unknown as ListConfigsApiResponse;
  const verticals = apiData.data?.verticals ?? [];

  for (const vertical of verticals) {
    for (const config of vertical.configs) {
      // Only include configs that have at least one version
      if (config.liveVersion !== null || config.draftExists) {
        configs.push({
          path: `${vertical.verticalKey}/${config.configType}`,
          configType: config.configType as ConfigType,
          verticalKey: vertical.verticalKey,
          version: config.liveVersion ?? 0,
          status: config.draftExists ? "draft" : "live",
          checksum: "", // Not provided by list endpoint
          createdBy: "", // Not provided by list endpoint
          createdAt: config.lastModifiedAt ?? "",
        });
      }
    }
  }

  return { success: true, data: configs };
}

// API response structure for getConfig
interface GetConfigApiResponse {
  readonly data: unknown; // The actual config data object
  readonly metadata: {
    readonly configType: string;
    readonly version: number;
    readonly status: "draft" | "live" | "archived";
    readonly checksum: string;
    readonly createdAt: string;
    readonly createdBy: string;
    readonly changeSummary?: string;
  };
  readonly timestamp: string;
  readonly requestId: string;
}

/**
 * Get a specific config by path.
 * Path format: "{verticalKey}/{configType}" e.g., "telco-mobile/schema"
 */
async function getConfig(request: GetConfigRequest): Promise<ApiResponse<ConfigResponse>> {
  const response = await apiRequest<GetConfigApiResponse>(`/api/v1/configs/${request.path}`);

  if (!response.success) {
    return response as ApiResponse<ConfigResponse>;
  }

  // API returns { data: <object>, metadata: {...} } - transform to { content: <string>, metadata }
  const apiData = response.data as unknown as GetConfigApiResponse;

  // Extract verticalKey and configType from path
  const [verticalKey, configType] = request.path.split("/");

  return {
    success: true,
    data: {
      content: JSON.stringify(apiData.data, null, 2),
      metadata: {
        path: request.path,
        verticalKey: verticalKey ?? "",
        configType: (configType ?? apiData.metadata.configType) as ConfigType,
        version: apiData.metadata.version,
        status: apiData.metadata.status,
        checksum: apiData.metadata.checksum,
        createdAt: apiData.metadata.createdAt,
        createdBy: apiData.metadata.createdBy,
      },
    },
  };
}

/**
 * Update (save) a config.
 * Path format: "{verticalKey}/{configType}" e.g., "telco-mobile/schema"
 */
async function updateConfig(
  request: UpdateConfigRequest
): Promise<ApiResponse<UpdateConfigResponse>> {
  // API expects PUT /api/v1/configs/:verticalKey/:configType/draft
  return apiRequest<UpdateConfigResponse>(`/api/v1/configs/${request.path}/draft`, {
    method: "PUT",
    body: {
      data: JSON.parse(request.content),
      changeSummary: request.changeSummary,
    },
  });
}

/**
 * Get version history for a config.
 * Path format: "{verticalKey}/{configType}" e.g., "telco-mobile/schema"
 */
async function getConfigHistory(
  request: GetConfigHistoryRequest
): Promise<ApiResponse<readonly ConfigVersionEntry[]>> {
  return apiRequest<readonly ConfigVersionEntry[]>(`/api/v1/configs/${request.path}/history`);
}

/**
 * Promote (publish) a config - makes the draft live.
 * Path format: "{verticalKey}/{configType}" e.g., "telco-mobile/schema"
 */
async function promoteConfig(
  request: PromoteConfigRequest
): Promise<ApiResponse<PromoteConfigResponse>> {
  // API expects POST /api/v1/configs/:verticalKey/:configType/publish
  return apiRequest<PromoteConfigResponse>(`/api/v1/configs/${request.path}/publish`, {
    method: "POST",
  });
}

/**
 * Create a new config.
 */
interface CreateConfigRequest {
  readonly verticalKey: string;
  readonly configType: ConfigType;
  readonly content: string;
}

interface CreateConfigResponse {
  readonly path: string;
  readonly version: number;
  readonly checksum: string;
  readonly createdAt: string;
}

async function createConfig(
  request: CreateConfigRequest
): Promise<ApiResponse<CreateConfigResponse>> {
  // API expects PUT /api/v1/configs/:verticalKey/:configType/draft
  return apiRequest<CreateConfigResponse>(
    `/api/v1/configs/${request.verticalKey}/${request.configType}/draft`,
    {
      method: "PUT",
      body: {
        data: JSON.parse(request.content),
      },
    }
  );
}

/**
 * Delete (discard) a config draft.
 * Path format: "{verticalKey}/{configType}" e.g., "telco-mobile/schema"
 */
async function deleteConfig(path: string): Promise<ApiResponse<{ readonly archivedAt: string }>> {
  // API expects DELETE /api/v1/configs/:verticalKey/:configType/draft
  return apiRequest<{ readonly archivedAt: string }>(`/api/v1/configs/${path}/draft`, {
    method: "DELETE",
  });
}

/**
 * Validate config content against schema.
 */
interface ValidateConfigRequest {
  readonly configType: ConfigType;
  readonly content: string;
}

interface ValidationResponse {
  readonly isValid: boolean;
  readonly errors: readonly {
    readonly path: string;
    readonly message: string;
    readonly code: string;
  }[];
}

async function validateConfig(
  request: ValidateConfigRequest
): Promise<ApiResponse<ValidationResponse>> {
  // For now, use the draft validate endpoint
  // This would need a path to validate - keeping simple for now
  return apiRequest<ValidationResponse>(`/api/v1/configs/validate`, {
    method: "POST",
    body: request,
  });
}

// ============================================================================
// Export
// ============================================================================

export const configApi = {
  listConfigs,
  getConfig,
  updateConfig,
  getConfigHistory,
  promoteConfig,
  createConfig,
  deleteConfig,
  validateConfig,
} as const;
